import React from 'react'

import { useIsTextTruncated } from '@inno-tools/react-hooks'
import { Avatar, AvatarBorderColors, AvatarSizes } from '@inno-ui/avatar'
import { Tooltip } from '@inno-ui/tooltip'
import { motion } from 'motion/react'
import { UserPartner } from 'shared/types/user'

import styles from './UserProfileTrigger.module.scss'

interface UserProfileTriggerProps {
  buttonRef?: React.Ref<HTMLButtonElement>
  isOpen: boolean
  onClick?: () => void
  popoverId?: string
  userName?: string | null
  userAvatar?: string | null
  company?: UserPartner
  placeholderText: string
}

export const UserProfileTrigger: React.FC<UserProfileTriggerProps> = ({
  buttonRef,
  isOpen,
  onClick,
  popoverId,
  userName,
  userAvatar,
  company,
  placeholderText
}) => {
  const { isTruncated, textRef } = useIsTextTruncated()

  return (
    <motion.button
      ref={buttonRef}
      type='button'
      className={styles.actionButton}
      onClick={onClick}
      aria-expanded={isOpen}
      aria-haspopup='dialog'
      aria-controls={isOpen ? popoverId : undefined}
      animate={{
        backgroundColor: isOpen ? 'var(--theme-color)' : 'transparent',
        color: isOpen ? 'white' : 'inherit'
      }}
      whileHover={
        !isOpen
          ? {
              backgroundColor: 'var(--theme-color-10)'
            }
          : {}
      }
      whileTap={{
        scale: 0.98
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25
      }}
    >
      <div className={styles.userImg}>
        <Avatar
          size={AvatarSizes.XL}
          url={userAvatar ?? ''}
          borderColor={AvatarBorderColors.Transparent}
        />
      </div>
      <div className={styles.name}>
        <p className={styles.userName}>{userName ?? placeholderText}</p>
        <Tooltip
          overlay={company?.name}
          disabled={!isTruncated}
          mouseEnterDelay={0.5}
        >
          <p
            className={styles.companyName}
            ref={textRef as React.RefObject<HTMLParagraphElement>}
          >
            {company?.name || '-'}
          </p>
        </Tooltip>
      </div>
      {!!company?.logo && (
        <div className={styles.companyLogo}>
          <img src={company.logo} alt={company.name} />
        </div>
      )}
    </motion.button>
  )
}
